import React, { useState, useEffect, useMemo } from 'react';
import './ManageEmployees.css';
import { usePaginatedFetch } from "./usePaginatedFetch";
import { API_ENDPOINTS } from './config/api.config';
import {
  TextField,
  Typography,
  Autocomplete,
  CircularProgress,
  MenuItem
} from '@mui/material';
import PropTypes from 'prop-types';
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";

function EmployeeFormModal({
  title,
  mode, // "add" | "edit"
  formData,
  onChange,
  onSubmit,
  onClose,
  selectedEmployee,
  isDrawer = false
}) {
  const {
    items: leads,
    hasMore: leadHasMore,
    loading: loadingLeads,
    fetchNext: fetchNextLeads,
    initFetch: initLeads,
  } = usePaginatedFetch({ url: API_ENDPOINTS.LEADS });

  const {
    items: pms,
    hasMore: PMHasMore,
    loading: loadingPMs,
    fetchNext: fetchNextPms,
    initFetch: initPms
  } = usePaginatedFetch({
    url: API_ENDPOINTS.PMS
  })

  const {
    items: projects,
    hasMore: projectHasMore,
    loading: loadingProjects,
    fetchNext: fetchNextProjects,
    initFetch: initProjects
  } = usePaginatedFetch({ url: API_ENDPOINTS.PROJECTS });

  const [leadOpen, setLeadOpen] = useState(false);
  const [projectOpen, setProjectOpen] = useState(false);
  const [pmOpen, setPmOpen] = useState(false);

  useEffect(() => {
    initLeads();
    initProjects();
    initPms();
  }, []);

  const handleLeadScroll = (event) => {
    const listboxNode = event.currentTarget;
    if (
      listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 5 &&
      leadHasMore &&
      !loadingLeads
    ) {
      fetchNextLeads();
    }
  };

  const handleProjectScroll = (event) => {
    const listboxNode = event.currentTarget;
    if (
      listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 5 &&
      projectHasMore &&
      !loadingProjects
    ) {
      fetchNextProjects();
    }
  };

  const handlePmScroll = (event) => {
    const listboxNode = event.currentTarget;
    if (
      listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 5 &&
      PMHasMore &&
      !loadingPMs
    ) {
      fetchNextPms();
    }
  };

  const mergedProjects = useMemo(() => {
    if (mode !== "edit" || !formData.projectName) return projects;
    const exists = projects.some((project) => project.project === formData.projectName);
    return exists ? projects : [{ project: formData.projectName }, ...projects];
  }, [formData.projectName, mode, projects]);

  const mergedLeads = useMemo(() => {
    if (mode !== "edit" || !formData.leadId) return leads;
    const exists = leads.some((lead) => lead.employeeId === formData.leadId);
    if (exists) return leads;
    const fallbackName =
      selectedEmployee?.lead?.leadName ||
      selectedEmployee?.lead?.employeeName ||
      String(formData.leadId);
    return [{ employeeId: formData.leadId, employeeName: fallbackName }, ...leads];
  }, [formData.leadId, leads, mode, selectedEmployee]);

  const mergedPms = useMemo(() => {
    if (mode !== "edit" || !formData.pmId) return pms;
    const exists = pms.some((pm) => pm.employeeId === formData.pmId);
    if (exists) return pms;
    const fallbackName =
      selectedEmployee?.pm?.pmName ||
      selectedEmployee?.pm?.employeeName ||
      String(formData.pmId);
    return [{ employeeId: formData.pmId, employeeName: fallbackName }, ...pms];
  }, [formData.pmId, mode, pms, selectedEmployee]);

  const formContent = (
    <div className={isDrawer ? "" : "employee-modal"}>
      {mode === 'edit' ? (
        <div className="employee-modal-header">
          <h3>{title}</h3>
          <button className="modal-close-btn" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#484B4D" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      ) : (
        <Typography className='text-bold'>Add Employee</Typography>
      )}

      <form className="employee-form" onSubmit={onSubmit}>
        <TextField
          fullWidth
          label="Employee ID"
          name="employeeId"
          value={formData.employeeId}
          onChange={onChange}
          margin="normal"
          size="small"
          required
        />

        <TextField
          fullWidth
          label="Name"
          name="employeeName"
          value={formData.employeeName}
          onChange={onChange}
          margin="normal"
          size="small"
          required
        />

        <TextField
          fullWidth
          label="Email"
          name="email"
          value={formData.email}
          onChange={onChange}
          margin="normal"
          size="small"
          required
        />

        {/* Project Autocomplete */}
        <Autocomplete
          open={projectOpen}
          onOpen={() => setProjectOpen(true)}
          onClose={() => setProjectOpen(false)}
          options={mergedProjects}
          getOptionLabel={(opt) => opt.project || ""}
          value={mergedProjects.find(p => p.project === formData.projectName) || null}
          onChange={(e, newValue) => {
            onChange({
              target: { name: 'projectName', value: newValue?.project || '' }
            });
          }}
          slotProps={{
            listbox: {
              onScroll: handleProjectScroll,
              style: { maxHeight: 200 }
            }
          }}
          loading={loadingProjects}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Project"
              margin="normal"
              size="small"
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingProjects ? <CircularProgress size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>)
                }}}
              required
            />
          )}
        />

        {/* Lead Autocomplete */}
        <Autocomplete
          open={leadOpen}
          onOpen={() => setLeadOpen(true)}
          onClose={() => setLeadOpen(false)}
          options={mergedLeads}
          getOptionLabel={(opt) => opt.employeeName || ""}
          value={mergedLeads.find(l => l.employeeId === formData.leadId) || null}
          onChange={(e, newValue) => {
            onChange({
              target: { name: 'leadId', value: newValue?.employeeId || '' }
            });
          }}
          slotProps={{
            listbox: {
              onScroll: handleLeadScroll,
              style: { maxHeight: 200 }
            }
          }}
          loading={loadingLeads}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Lead"
              margin="normal"
              size="small"
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingLeads ? <CircularProgress size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>)}
              }}
              required
            />
          )}
        />

        <input type="hidden" name="leadId" value={formData.leadId} />

        <Autocomplete
          open={pmOpen}
          onOpen={() => setPmOpen(true)}
          onClose={() => setPmOpen(false)}
          options={mergedPms}
          getOptionLabel={(opt) => opt.employeeName || ""}
          value={mergedPms.find(l => l.employeeId === formData.pmId) || null}
          onChange={(e, newValue) => {
            onChange({
              target: { name: 'pmId', value: newValue?.employeeId || '' }
            });
          }}
          slotProps={{
            listbox: {
              onScroll: handlePmScroll,
              style: { maxHeight: 200 }
            }
          }}
          loading={loadingPMs}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Project Manager"
              margin="normal"
              size="small"
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingPMs ? <CircularProgress size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>)}
              }}
              required
            />
          )}
        />
        <input type="hidden" name="pmId" value={formData.pmId} />

        <div className="form-group">
          {/* <TextField
            fullWidth
            label="Shift"
            type="time"
            name="shiftTime"
            value={formData.shiftTime}
            onChange={onChange}
            margin="normal"
            size="small"
            InputLabelProps={{
              shrink: true, // important for time field
            }}
          /> */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <TimePicker
              label="Shift"
              value={
                formData.shiftTime
                  ? dayjs(`2000-01-01T${formData.shiftTime}`)
                  : null
              }
              onChange={(newValue) => {
                onChange({
                  target: {
                    name: "shiftTime",
                    value: newValue ? newValue.format("HH:mm") : ""
                  }
                });
              }}
              slotProps={{
                textField: {
                  size: "small",
                  margin: "normal",
                  fullWidth: true
                }
              }}
            />
          </LocalizationProvider>
          {/* <label>Shift</label>
          <input type="time" name="shiftTime" value={formData.shiftTime} onChange={onChange} /> */}
        </div>

        <div className="form-group">
          <TextField
            fullWidth
            select
            label="Location"
            name="location"
            value={formData.location}
            onChange={onChange}
            margin="normal"
            size="small"
          >
            <MenuItem value="Chennai">Chennai</MenuItem>
            <MenuItem value="CBE">CBE</MenuItem>
          </TextField>
        </div>

        <div className="form-actions">
          {mode === "edit" && (
          <button type="button" className="btn-cancel" onClick={onClose}>
            Cancel
          </button>
          )}
          <button type="submit" className="btn-submit">
            {mode === "add" ? "Create Employee" : "Update Employee"}
          </button>
        </div>
      </form>
    </div>
  );

  if (isDrawer) return formContent;

  return <div className="employee-modal-overlay">{formContent}</div>;
}

EmployeeFormModal.propTypes = {
  title: PropTypes.string.isRequired,
  mode: PropTypes.oneOf(['add', 'edit']).isRequired,
  formData: PropTypes.object.isRequired, // 'object' covers the whole formData structure
  employees: PropTypes.array, // This seems optional based on your usage, so no isRequired
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedEmployee: PropTypes.object, // Optional
  isDrawer: PropTypes.bool
};
export default EmployeeFormModal;

